/**
 * TanStack Query Hook for Female Founders KPI
 *
 * This hook provides access to female founders KPI data from the database,
 * including percentage of startups with female founders and year-over-year change.
 *
 * Database Source: web.v_kpi_female_founders_v1 view
 *
 * @module useKpiFemaleFoundersV1
 * @category Database Hooks
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Interface for Female Founders KPI data
 */
interface KpiFemaleFounders {
  female_founders_percentage: number;
  female_founders_change_percentage: number;
}

/**
 * Fetches female founders KPI data from the database
 *
 * @returns {Promise<KpiFemaleFounders>} KPI data object with percentage and change
 * @throws {Error} If the database query fails or returns invalid data
 * @internal
 */
const fetchKpiFemaleFounders = async (): Promise<KpiFemaleFounders> => {
  const { data, error } = await supabase
    .schema('web')
    .from('v_kpi_female_founders_v1')
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to fetch female founders KPI: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from female founders KPI query');
  }

  return data;
};

/**
 * React Hook for Female Founders KPI Data
 *
 * Fetches and caches the female founders KPI data using TanStack Query.
 * Includes percentage of startups with female founders and YoY change.
 *
 * Query Configuration:
 * - Query Key: ['kpi', 'female-founders', 'v1']
 * - Stale Time: 1 hour (3600000ms)
 * - Refetch on Window Focus: false
 * - Refetch on Mount: false
 * - Retry Attempts: 2
 *
 * @returns {UseQueryResult<KpiFemaleFounders>} TanStack Query result object
 *
 * @example
 * ```typescript
 * const { data, isLoading, isError } = useKpiFemaleFoundersV1();
 *
 * if (isLoading) return <Spinner />;
 * if (isError) return <ErrorMessage />;
 *
 * return (
 *   <div>
 *     <p>Female Founders: {data.female_founders_percentage}%</p>
 *     <p>Change: +{data.female_founders_change_percentage}%</p>
 *   </div>
 * );
 * ```
 */
export const useKpiFemaleFoundersV1 = (): UseQueryResult<KpiFemaleFounders> => {
  return useQuery<KpiFemaleFounders>({
    queryKey: ['kpi', 'female-founders', 'v1'],
    queryFn: fetchKpiFemaleFounders,
    staleTime: 3600000, // 1 hour in milliseconds
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });
};
